import { t } from "@/lib/i18n/interpolate";
import type {
  DocBlock,
  DocListItem,
  DocSection,
  DocumentModel,
} from "@/lib/tool-kit/document";
import { fillFrame, joinSeries, tidy } from "@/lib/tool-kit/essay/sentence";
import { splitItems } from "@/lib/tool-kit/essay-templates/text";
import {
  elaborated,
  isFilled,
  itemOrPrompt,
  lettered,
  thesisReady,
} from "@/lib/tool-kit/lit-essays/common";

/**
 * An AP History long essay question (LEQ) outline built around a reasoning process.
 *
 * The LEQ is scored out of six (College Board scoring guidelines, 2025), shared by AP
 * U.S. History, AP World History: Modern and AP European History:
 *
 * | Row | Point(s) | Earned by |
 * | --- | --- | --- |
 * | A Thesis/Claim | 1 | A defensible claim that establishes a line of reasoning |
 * | B Contextualization | 1 | A broader context described — more than a phrase |
 * | C Evidence | 2 | Two specific examples relevant to the prompt (1); using at least two to support an argument (2) |
 * | D Analysis and Reasoning | 2 | Historical reasoning — comparison, causation, or continuity and change over time — to frame or structure the argument (1); complex understanding (2) |
 *
 * The checks are a planning aid. "Specific" and "more than a phrase" are judgements for
 * a reader, so the word-count tests here are rough proxies and the labels say so.
 */

export type ReasoningProcess = "comparison" | "causation" | "ccot";

export const REASONING_PROCESSES: readonly ReasoningProcess[] = [
  "comparison",
  "causation",
  "ccot",
];

export type LeqComplexityMove =
  | ""
  | "perspectives"
  | "multiple"
  | "both"
  | "connections"
  | "fourPieces"
  | "differentViews";

export const LEQ_COMPLEXITY_MOVES: readonly Exclude<LeqComplexityMove, "">[] = [
  "perspectives",
  "multiple",
  "both",
  "connections",
  "fourPieces",
  "differentViews",
];

export interface LeqParagraph {
  id: number;
  /** The analytic category: "Market integration". */
  category: string;
  claim: string;
  /** Specific examples, one per line, each with a few words saying what it was. */
  evidence: string;
  /** How the evidence supports the claim, in terms of the reasoning process. */
  explanation: string;
}

export interface LeqInput {
  prompt: string;
  process: ReasoningProcess;
  context: string;
  thesis: string;
  paragraphs: readonly LeqParagraph[];
  complexityMove: LeqComplexityMove;
  complexityNote: string;
}

export const LEQ_MIN_PARAGRAPHS = 2;
export const LEQ_MAX_PARAGRAPHS = 4;
const MIN_THESIS_WORDS = 8;
const MIN_ELABORATION_WORDS = 6;
/** A named example needs at least a few words: what it was, not just its name. */
const MIN_EXAMPLE_WORDS = 3;

/** Thesis frames by reasoning process; `{categories}` is the paragraph categories. */
export const LEQ_THESIS_FRAMES: Readonly<
  Record<ReasoningProcess, readonly string[]>
> = {
  comparison: [
    "Although ___ and ___ [the two things compared] were similar in ___, they differed most in {categories}, because ___ [your reason].",
    "___ and ___ were alike in {categories}, but ___ [the key difference] set them apart.",
  ],
  causation: [
    "___ [the development] resulted mainly from {categories}, with ___ the most important.",
    "Although ___ [a short-term cause] triggered ___ [the development], the deeper cause was ___ [a long-term cause].",
  ],
  ccot: [
    "From ___ to ___ [the period], ___ [what changed] changed in {categories}, but ___ [what stayed the same] continued.",
    "The most significant change was ___, which began with ___ [the turning point], while ___ remained constant.",
  ],
};

export function blankParagraph(id: number): LeqParagraph {
  return { id, category: "", claim: "", evidence: "", explanation: "" };
}

/** The paragraph categories as analytic categories, with stand-ins for unnamed ones. */
export function buildLeqThesisFrames(
  process: ReasoningProcess,
  paragraphs: readonly LeqParagraph[],
  and: string,
  stand: (n: number) => string,
  frames: readonly string[] = LEQ_THESIS_FRAMES[process]
): string[] {
  const categories = joinSeries(
    paragraphs.map((paragraph, index) =>
      tidy(paragraph.category) === ""
        ? stand(index + 1)
        : tidy(paragraph.category)
    ),
    and
  );
  return frames
    .map((frame) => fillFrame(frame, { categories }))
    .filter((text): text is string => text !== null);
}

/* ------------------------------------------------------------------ checks */

export interface LeqChecks {
  thesis: boolean;
  contextualization: boolean;
  /** Specific examples listed across all paragraphs. */
  examples: number;
  /** Examples inside a paragraph that has both a claim and an explanation. */
  examplesUsed: number;
  evidenceOne: boolean;
  evidenceTwo: boolean;
  reasoning: boolean;
  complexity: boolean;
  /** Points the outline is set up to earn, 0–6. */
  planned: number;
}

function examplesOf(paragraph: LeqParagraph): string[] {
  return splitItems(paragraph.evidence).filter((line) =>
    elaborated(line, MIN_EXAMPLE_WORDS)
  );
}

/** Planning checks against the six rubric points. */
export function leqChecks(input: LeqInput): LeqChecks {
  const examples = input.paragraphs.flatMap(examplesOf).length;
  const supported = input.paragraphs.filter(
    (paragraph) =>
      isFilled(paragraph.claim) &&
      elaborated(paragraph.explanation, MIN_ELABORATION_WORDS)
  );
  const examplesUsed = supported.flatMap(examplesOf).length;
  const framed = input.paragraphs.filter((p) => isFilled(p.claim)).length;
  const checks = {
    thesis: thesisReady(input.thesis, input.prompt, MIN_THESIS_WORDS),
    contextualization: elaborated(input.context, MIN_ELABORATION_WORDS),
    examples,
    examplesUsed,
    evidenceOne: examples >= 2,
    evidenceTwo: examplesUsed >= 2,
    reasoning: framed >= LEQ_MIN_PARAGRAPHS,
    complexity:
      input.complexityMove !== "" &&
      elaborated(input.complexityNote, MIN_ELABORATION_WORDS),
  };
  const planned = [
    checks.thesis,
    checks.contextualization,
    checks.evidenceOne,
    checks.evidenceTwo,
    checks.reasoning,
    checks.complexity,
  ].filter(Boolean).length;
  return { ...checks, planned };
}

/* ------------------------------------------------------------------ document */

export interface LeqLabels {
  docTitle: string;
  subtitle: string;
  headings: {
    prompt: string;
    introduction: string;
    body: string;
    complexity: string;
    rubric: string;
  };
  items: {
    process: string;
    context: string;
    thesis: string;
    claim: string;
    evidence: string;
    explanation: string;
    link: string;
    move: string;
  };
  processes: Readonly<Record<ReasoningProcess, string>>;
  processGuidance: Readonly<Record<ReasoningProcess, string>>;
  claimPrompts: Readonly<Record<ReasoningProcess, string>>;
  explanationPrompts: Readonly<Record<ReasoningProcess, string>>;
  complexityMoves: Readonly<Record<Exclude<LeqComplexityMove, "">, string>>;
  prompts: {
    prompt: string;
    context: string;
    thesis: string;
    evidence: string;
    link: string;
    complexity: string;
  };
  guidance: { context: string; evidence: string };
  rubricNote: string;
  plannedLine: string;
  rubric: {
    thesis: string;
    contextualization: string;
    evidenceOne: string;
    evidenceTwo: string;
    reasoning: string;
    complexity: string;
  };
  categoryStand: string;
}

function bodySection(
  paragraph: LeqParagraph,
  index: number,
  process: ReasoningProcess,
  labels: LeqLabels
): DocSection {
  const category = tidy(paragraph.category);
  const claim: DocListItem = isFilled(paragraph.claim)
    ? itemOrPrompt(labels.items.claim, tidy(paragraph.claim), "")
    : { text: labels.claimPrompts[process], prompt: true };
  const lines = splitItems(paragraph.evidence);
  const evidence: DocListItem =
    lines.length === 0
      ? { text: labels.prompts.evidence, prompt: true }
      : {
          text: [{ text: `${labels.items.evidence}:`, bold: true }],
          children: {
            style: "bullet",
            items: lines.map((line) => ({ text: line })),
          },
        };
  const explanation: DocListItem = isFilled(paragraph.explanation)
    ? itemOrPrompt(labels.items.explanation, tidy(paragraph.explanation), "")
    : { text: labels.explanationPrompts[process], prompt: true };
  return {
    heading: t(labels.headings.body, {
      n: index + 1,
      category:
        category === "" ? t(labels.categoryStand, { n: index + 1 }) : category,
    }),
    blocks: [
      lettered([
        claim,
        evidence,
        explanation,
        { text: labels.prompts.link, prompt: true },
      ]),
    ],
  };
}

function rubricSection(input: LeqInput, labels: LeqLabels): DocSection {
  const checks = leqChecks(input);
  return {
    heading: labels.headings.rubric,
    blocks: [
      { kind: "guidance", text: labels.rubricNote },
      {
        kind: "checklist",
        items: [
          { text: labels.rubric.thesis, checked: checks.thesis },
          {
            text: labels.rubric.contextualization,
            checked: checks.contextualization,
          },
          { text: labels.rubric.evidenceOne, checked: checks.evidenceOne },
          { text: labels.rubric.evidenceTwo, checked: checks.evidenceTwo },
          { text: labels.rubric.reasoning, checked: checks.reasoning },
          { text: labels.rubric.complexity, checked: checks.complexity },
        ],
      },
      {
        kind: "paragraph",
        text: t(labels.plannedLine, { planned: checks.planned }),
      },
    ],
  };
}

function complexitySection(input: LeqInput, labels: LeqLabels): DocSection {
  const blocks: DocBlock[] = [];
  if (input.complexityMove !== "") {
    blocks.push({
      kind: "fields",
      fields: [
        {
          label: labels.items.move,
          value: labels.complexityMoves[input.complexityMove],
        },
      ],
    });
  }
  blocks.push(
    isFilled(input.complexityNote)
      ? { kind: "paragraph", text: tidy(input.complexityNote) }
      : { kind: "prompt", text: labels.prompts.complexity }
  );
  return { heading: labels.headings.complexity, blocks };
}

/** The LEQ plan as a document. */
export function buildLeqOutline(
  input: LeqInput,
  labels: LeqLabels
): DocumentModel {
  const sections: DocSection[] = [
    {
      heading: labels.headings.prompt,
      blocks: [
        isFilled(input.prompt)
          ? { kind: "paragraph", text: tidy(input.prompt) }
          : { kind: "prompt", text: labels.prompts.prompt },
        {
          kind: "fields",
          fields: [
            {
              label: labels.items.process,
              value: labels.processes[input.process],
            },
          ],
        },
        { kind: "guidance", text: labels.processGuidance[input.process] },
      ],
    },
    {
      heading: labels.headings.introduction,
      blocks: [
        { kind: "guidance", text: labels.guidance.context },
        lettered([
          itemOrPrompt(
            labels.items.context,
            tidy(input.context),
            labels.prompts.context
          ),
          itemOrPrompt(
            labels.items.thesis,
            tidy(input.thesis),
            labels.prompts.thesis
          ),
        ]),
      ],
    },
    ...input.paragraphs.map((paragraph, index) =>
      bodySection(paragraph, index, input.process, labels)
    ),
    complexitySection(input, labels),
    rubricSection(input, labels),
  ];
  return { title: labels.docTitle, subtitle: labels.subtitle, sections };
}
